import React, { useState, useEffect } from "react";

import leftBackgroundImage from '/img/project_authSideImage.jpg'; // background on left

/* -------- Redux -------- */
import { useDispatch, useSelector } from "react-redux";
import { validatePasswordToken, resetPassword, loginError, clearUserSuccess, clearUserErrors } from "../../store/actions";

/* -------- packages -------- */
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import {
  Row,
  Col,
  CardBody,
  Card,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
  InputGroup,
  Spinner,
} from "reactstrap";
import { getErrorMessage } from "../../utils/utils";
/* ------------------------- */

export default function ResetPassword() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token } = useParams();
  
  const parameters = useSelector(state => state.Parameters);
  document.title = `${t('resetPassword')} | ${parameters.appName}`; //meta title
  
  const passwordReset = useSelector(state => state.Users);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordRepeat, setShowPasswordRepeat] = useState(false);
  const [error, setError] = useState('');
  const [tokenValidated, setTokenValidated] = useState(false);

  // États pour gérer les erreurs persistantes comme dans DynamicForm
  const [allErrors, setAllErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  useEffect(() => {
    // Validate token when component mounts
    if (token) {
      dispatch(validatePasswordToken(token));
    }
  }, [token]);

  useEffect(() => {
    if (passwordReset.tokenValid) {
      setTokenValidated(true);
    }
  }, [passwordReset.tokenValid]);

  const resetForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      password: '',
      passwordRepeat: '',
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(10, t('errorPasswordMinLength'))
        .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9]).{8,}$/, t('errorPasswordRequirements'))
        .required(t('errorEnterPassword')),
      passwordRepeat: Yup.string()
        .oneOf([Yup.ref('password')], t('errorPasswordsMatch'))
        .required(t('errorEnterValidPasswordRepeat')),
    }),
    validateOnChange: false, // Désactive la validation automatique sur onChange comme dans DynamicForm
    validateOnBlur: true,
    onSubmit: (values) => {
      dispatch(resetPassword({
        token: token,
        userId: passwordReset.userId,
        password: values.password,
        passwordRepeat: values.passwordRepeat
      }, navigate));
    },
  });

  const inputPasswordError = (resetForm.touched.password && resetForm.errors.password);
  const inputPasswordRepeatError = (resetForm.touched.passwordRepeat && resetForm.errors.passwordRepeat);

  useEffect(() => {
    if (error !== '') {
      dispatch(loginError(""));
      setError('');
    } else if (passwordReset.errorUser) {
      setError(passwordReset.errorUser);
      toast.error(getErrorMessage(passwordReset.errorUser));
      dispatch(clearUserErrors());
    } else if (passwordReset.passwordResetSuccess) {
      toast.success(t('passwordResetSuccess'));
      dispatch(clearUserSuccess());
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    }
  }, [passwordReset.loadingUser, passwordReset.errorUser, passwordReset.passwordResetSuccess]); // Utilise loadingUser

  // Gestion des erreurs de validation comme dans DynamicForm
  useEffect(() => {
    // Update touched fields
    setTouchedFields(prev => ({
      ...prev,
      ...resetForm.touched
    }));

    // Capture new errors and merge them with previous ones
    if (resetForm.errors && Object.keys(resetForm.errors).length > 0) {
      setAllErrors(prev => {
        const newErrors = { ...prev };
        
        // Add or update errors for touched fields
        Object.keys(resetForm.errors).forEach(fieldKey => {
          if (resetForm.touched[fieldKey]) {
            newErrors[fieldKey] = resetForm.errors[fieldKey];
          }
        });
        
        // Remove errors for fields that are now valid AND touched
        Object.keys(prev).forEach(fieldKey => {
          if (resetForm.touched[fieldKey] && !resetForm.errors[fieldKey]) {
            delete newErrors[fieldKey];
          }
        });
        
        return newErrors;
      });
    } else {
      // If no new errors, clean errors for touched fields that are now valid
      setAllErrors(prev => {
        const newErrors = { ...prev };
        Object.keys(resetForm.touched).forEach(fieldKey => {
          if (resetForm.touched[fieldKey] && !resetForm.errors[fieldKey]) {
            delete newErrors[fieldKey];
          }
        });
        return newErrors;
      });
    }
  }, [resetForm.errors, resetForm.touched]);

  // Fonctions utilitaires pour la gestion des erreurs comme dans DynamicForm
  const getFieldError = (fieldKey) => {
    // Priority to current Formik errors, otherwise use preserved errors
    return resetForm.errors[fieldKey] || allErrors[fieldKey];
  };

  const hasFieldError = (fieldKey) => {
    return !!(resetForm.errors[fieldKey] || allErrors[fieldKey]);
  };

  const shouldShowError = (fieldKey) => {
    return (resetForm.touched[fieldKey] || touchedFields[fieldKey]) && hasFieldError(fieldKey);
  };

  // Gestion des erreurs API comme dans DynamicForm
  useEffect(() => {
    if (passwordReset.errorUser && passwordReset.errorUser.data && passwordReset.errorUser.data.errors) {
      passwordReset.errorUser.data.errors.forEach(err => {
        if (err.code && err.key) {
          resetForm.setFieldError(err.key, t(err.code));
        }
      });
    }
  }, [passwordReset.errorUser]);

  if (!tokenValidated && !passwordReset.loadingUser) {
    return (
      <Container fluid className="p-0 m-0 vh-100 overflow-hidden d-flex flex-column authenticationPage">
      <Row className="h-100 m-0">
        <Col lg={8} className="d-none d-lg-block p-0">
          <div className="auth-full-bg p-0 h-100">
            <div className="d-flex h-100 p-0">
              <img src={leftBackgroundImage} alt="" className="img-fluid" />
              <span className="logoLogin">
                <img src={parameters.logo} alt="logo" width={300}  className="logo"/>
            </span>
            </div>
          </div>
        </Col>

        <Col lg={4} className="p-0 authenticationContainer h-100 d-flex flex-column justify-content-between flex-grow-1">
          <Row className={"d-flex flex-column justify-content-center align-items-center h-25"}>
            <Col className={"cardHeader mg-0"}>
              <Col lg={12} className="col p-0">
                <div className="text-primary">
                  <h5 className="text-primary">{t('resetPassword')}</h5>
                  <p>{t('resetPasswordText')}</p>
                </div>
              </Col>
            </Col>
          </Row>
          
          <Row className={" d-flex flex-column align-items-center p-3 h-75"}>
            <Card className="overflow-hidden interactionContainer">

              <div className="text-center p-4 w-100 d-flex flex-column justify-content-center align-items-center">
                <h5 className="text-danger">{t('invalidToken')}</h5>
                <p>{t('tokenExpiredOrInvalid')}</p>
                <button 
                  className="btn btn-primary mt-3 btnLText" 
                  onClick={() => navigate('/forgotPassword')}
                >
                  {t('requestNewPasswordReset')}
                </button>
              </div>

            </Card>
          </Row>

          <Row className="h-25">
              <div className="cardFooter">
                <button className="btn btn-secondary btn-block" type="button" onClick={() => navigate('/login')}>
                  {t('backToLogin')}
                </button>
              </div>
          </Row>

        </Col>
      </Row>

      <ToastContainer />
    </Container>
    );
  }

  return (
    <Container fluid className="p-0 m-0 vh-100 overflow-hidden d-flex flex-column authenticationPage">
      <Row className="h-100 m-0">
        <Col lg={8} className="d-none d-lg-block p-0">
          <div className="auth-full-bg p-0 h-100">
            <div className="d-flex h-100 p-0">
              <img src={leftBackgroundImage} alt="" className="img-fluid" />
              <span className="logoLogin">
                <img src={parameters.logo} alt="logo" width={300}  className="logo"/>
            </span>
            </div>
          </div>
        </Col>

        <Col lg={4} className="p-0 authenticationContainer h-100 d-flex flex-column justify-content-between flex-grow-1">
          <Row className={"d-flex flex-column justify-content-center align-items-center h-25"}>
            <Col className={"cardHeader mg-0"}>
              <Col lg={12} className="col p-0">
                <div className="text-primary">
                  <h5 className="text-primary">{t('resetPassword')}</h5>
                  <p>{t('resetPasswordText')}</p>
                </div>
              </Col>
            </Col>
          </Row>
          
          <Row className={" d-flex flex-column align-items-center p-3 h-75"}>
          <Card className="overflow-hidden">
                      
            <CardBody className="pt-0 interactionContainer">
                    {passwordReset.loadingUser && !tokenValidated ? (
                      <div className="text-center p-4">
                        <Spinner color="primary" />
                        <p className="mt-2">{t('validatingToken')}</p>
                      </div>
                    ) : (
                      <Form
                        className="form-horizontal signIn"
                        onSubmit={(e) => {
                          e.preventDefault();
                          resetForm.handleSubmit();
                          return false;
                        }}
                      >
                        <div className="mb-3">
                          <Label className="form-label">{t('password')}</Label>
                          <InputGroup>
                            <Input
                              name="password"
                              className="input-password"
                              placeholder="********"
                              autoComplete="new-password"
                              value={resetForm.values.password || ""}
                              type={showPassword ? "text" : "password"}
                              onChange={resetForm.handleChange}
                              onBlur={resetForm.handleBlur}
                              invalid={shouldShowError('password')}
                              data-cy="reset-password"
                            />
                            {/* Show password toggle button for password field */}
                            {(
                              <span className='passwordVisibility' onClick={() => setShowPassword(!showPassword)}>
                                <a id="projTogglePasswordVisibility" style={{ padding: 0 }}>
                                  <i className={showPassword ? 'mdi mdi-eye' : 'mdi mdi-eye-off'} />
                                </a>
                              </span>
                            )}
                          </InputGroup>
                          {/* Message d'information sur le mot de passe comme dans DynamicForm */}
                          <FormFeedback className={'d-block text-info'}>
                            {t('STRONG_PASSWORD')}
                          </FormFeedback>
                          {shouldShowError('password') && (
                            <FormFeedback className="d-block" type="invalid" data-cy="reset-passwordError">
                              {getFieldError('password')}
                            </FormFeedback>
                          )}
                        </div>
                        
                        <div className="mb-3">
                          <Label className="form-label">{t('passwordRepeat')}</Label>
                          <InputGroup>
                            <Input
                              name="passwordRepeat"
                              className="input-password"
                              placeholder="********"
                              autoComplete="new-password"
                              value={resetForm.values.passwordRepeat || ""}
                              type={showPasswordRepeat ? "text" : "password"}
                              onChange={resetForm.handleChange}
                              onBlur={resetForm.handleBlur}
                              invalid={shouldShowError('passwordRepeat')}
                              data-cy="reset-passwordRepeat"
                            />
                            {/* Show password toggle button for password repeat field */}
                            {(
                              <span className='passwordVisibility' onClick={() => setShowPasswordRepeat(!showPasswordRepeat)}>
                                <a id="projTogglePasswordVisibility" style={{ padding: 0 }}>
                                  <i className={showPasswordRepeat ? 'mdi mdi-eye' : 'mdi mdi-eye-off'} />
                                </a>
                              </span>
                            )}
                          </InputGroup>
                          {shouldShowError('passwordRepeat') && (
                            <FormFeedback className="d-block" type="invalid" data-cy="reset-passwordRepeatError">
                              {getFieldError('passwordRepeat')}
                            </FormFeedback>
                          )}
                        </div>
                        
                        <div className="mt-3 d-grid">
                          <button 
                            className="btn btn-primary btn-block btnLText justify-content-center d-flex align-items-center" 
                            type="submit" 
                            data-cy="reset-submit"
                            disabled={passwordReset.loadingUser}
                          >
                            {passwordReset.loadingUser ? (
                              <>
                                <Spinner size="sm" className="me-2" />
                              </>
                            ) : (
                              t('resetPassword')
                            )}
                          </button>
                        </div>
                      </Form>
                    )}
                    
              </CardBody>
            </Card>
          </Row>

          <Row className="h-25">
              <div className="cardFooter">
                <button className="btn btn-secondary btn-block" type="button" onClick={() => navigate('/login')}>
                  {t('backToLogin')}
                </button>
              </div>
          </Row>

        </Col>
      </Row>

      <ToastContainer />
    </Container>
  );

  
}